import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useIdleTimer } from 'react-idle-timer';
import SecureLS from 'secure-ls';
import CommanModal from '../../components/CommanModal';
import {
  RootState,
  setRememberMe,
  rewardsFetched,
  resetUser,
  signOutRequest,
  resetValidatePasscodeSlice,
  useraccountDetailsRequest,
  getChannelId,
} from '@rb-serve-app/core';
import FAQsModal from '../../components/FAQsModal';
import { buildSignInRedirectUrl } from '../../utility/authRedirect';

// Constants - matching React Native implementation
const INACTIVITY_TIME_LIMIT = 8 * 60 * 1000; // 8 minutes of inactivity
const MODAL_TIMEOUT_SECONDS = 115; // 2 minutes countdown (in seconds)
const TOKEN_REFRESH_BUFFER = 2 * 60 * 1000; // 2 minutes before token expiry
const TOKEN_CHECK_INTERVAL = 30 * 1000; // Check token expiry every 30 seconds

const ls = new SecureLS({ encodingType: 'aes' });

type ProtectedRouteProps = {
  element: React.ReactElement;
};

interface TokenData {
  accessToken: string;
  refreshToken: string;
  expiryTime: string;
}

const ProtectedRoute = ({ element }: ProtectedRouteProps) => {
  const location = useLocation();
  const { isAuthenticated, accountId, customerId, isSessionTimeout } =
    useSelector((state: any) => state.user);
  const dispatch = useDispatch();
  const { isBiometricEnabled, rememberMe, userName, accountDetails } =
    useSelector((state: RootState) => state.user || {});

  // State management
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalCountdown, setModalCountdown] = useState(MODAL_TIMEOUT_SECONDS);

  // Refs for managing timers and state - following React Native pattern
  const mounted = useRef(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const tokenRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sessionValidationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const isRefreshingRef = useRef(false);
  const lastRefreshTimeRef = useRef<number>(Date.now());
  const countdownRef = useRef<number>(MODAL_TIMEOUT_SECONDS);
  const isModalActiveRef = useRef(false);

  // Clear all timers utility
  const clearTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
      tokenRefreshTimerRef.current = null;
    }
    if (sessionValidationIntervalRef.current) {
      clearInterval(sessionValidationIntervalRef.current);
      sessionValidationIntervalRef.current = null;
    }
  }, []);

  // Reset all session state
  const resetAllSessionState = useCallback(() => {
    clearTimers();

    // Reset all refs
    isRefreshingRef.current = false;
    lastRefreshTimeRef.current = Date.now();
    countdownRef.current = MODAL_TIMEOUT_SECONDS;
    isModalActiveRef.current = false;

    // Reset modal UI
    setIsModalVisible(false);
    setModalCountdown(MODAL_TIMEOUT_SECONDS);
  }, [clearTimers]);

  // Handle complete logout - following React Native pattern
  const handleLogout = useCallback(
    (reason = 'Unknown') => {
      console.log(`Logging out due to: ${reason}`);

      // Prevent multiple logouts
      if (!isAuthenticated) return;

      resetAllSessionState();

      // Prepare logout API data before resetting user state
      const deviceId = localStorage.getItem('deviceId');
      const userData = {
        username: userName, // Get userName before reset
        deviceId,
        channel: getChannelId,
        flow: 'PATH_SIGNOUT_API',
      };

      try {
        // Make logout API call first
        dispatch(
          signOutRequest({ apiName: 'sign_out', data: userData, accountId })
        );
        console.log('Logout API call completed');
      } catch (error) {
        console.error('Logout API call failed:', error);
        // Continue with logout process even if API fails
      } finally {
        // Reset User data after API call
        dispatch(rewardsFetched(null));
        dispatch(resetUser());
        dispatch(resetValidatePasscodeSlice());

        // Clear storage — tokens/user are cleaned by the signOutSaga after the API call
        localStorage.removeItem('sco');

        if (!isBiometricEnabled && !rememberMe) {
          dispatch(setRememberMe(true));
        }

        window.location.href = '/sign-in';
      }
    },
    [
      dispatch,
      isBiometricEnabled,
      rememberMe,
      userName,
      isAuthenticated,
      resetAllSessionState,
    ]
  );

  // Token refresh logic - matching React Native implementation
  const refreshToken = useCallback(() => {
    console.log(
      'refreshToken>|>|>| during idle check - isModalActive:',
      isModalActiveRef.current,
      'isRefreshing:',
      isRefreshingRef.current,
      'isAuthenticated:',
      !isAuthenticated,
      'accountId:',
      !accountId
    );

    if (isRefreshingRef.current || !isAuthenticated || !accountId) return;

    isRefreshingRef.current = true;

    console.log('🔄 Token refresh during idle - modal should not be affected');

    // Just dispatch - middleware handles token regeneration (same as React Native)
    dispatch(useraccountDetailsRequest({ accountId, customerId }));

    // Update last refresh time
    lastRefreshTimeRef.current = Date.now();

    if (mounted.current) {
      // Reset isRefreshingRef after a delay, but don't affect modal state
      setTimeout(() => {
        isRefreshingRef.current = false;
        console.log(
          'Token refresh completed, isModalActive still:',
          isModalActiveRef.current
        );
      }, 2000); // Increased timeout to ensure refresh completes
    }
  }, [isAuthenticated, accountId, customerId, dispatch]);

  // Session validation - adapted from React Native validateSession
  const validateSession = useCallback(async () => {
    if (!isAuthenticated || !mounted.current) return;

    try {
      // 1. Get and validate tokens
      const tokensString = ls.get('tokens');
      if (!tokensString) {
        console.log('No tokens found');
        return;
      }

      // 2. Parse tokens
      let tokens: TokenData;
      try {
        tokens =
          typeof tokensString === 'string'
            ? JSON.parse(tokensString)
            : tokensString;
      } catch (parseError) {
        console.error('Failed to parse tokens:', parseError);
        return;
      }

      // 3. Check expiration
      const currentTime = Date.now();
      const expiryTime = parseInt(tokens.expiryTime);
      const timeLeft = expiryTime - currentTime;

      console.log({
        currentTime: new Date(currentTime).toISOString(),
        expiryTime: new Date(expiryTime).toISOString(),
        timeLeftMs: timeLeft,
        timeLeftSecond: timeLeft / 1000,
        timeLeftMin: timeLeft / 1000 / 60,
      });

      // 4. Handle expired tokens
      if (timeLeft <= 0) {
        console.log('Token has expired');
        handleLogout('Token expired');
        return;
      }

      // 5. Set up refresh if needed
      if (timeLeft <= TOKEN_REFRESH_BUFFER && !tokenRefreshTimerRef.current) {
        const refreshDelay = Math.max(0, timeLeft / 2); // Execute refreshToken in last 30 seconds of buffer time
        tokenRefreshTimerRef.current = setTimeout(() => {
          refreshToken();
          tokenRefreshTimerRef.current = null;
        }, refreshDelay);
      }
    } catch (error) {
      console.error('Session validation failed:', error);
    }
  }, [isAuthenticated, handleLogout, refreshToken]);

  // Handle user idle state - following React Native onInactive pattern
  const onInactive = useCallback(() => {
    console.log('🚨 onInactive>>> triggered at:', new Date().toISOString());
    console.log('Modal state check:', {
      isModalActiveRef: isModalActiveRef.current,
      isRefreshingRef: isRefreshingRef.current,
      isModalVisible: isModalVisible,
    });

    // Prevent multiple inactivity handlers when modal is showing
    if (isModalActiveRef.current) {
      console.log('❌ Modal already active, skipping inactivity handler');
      return;
    }

    console.log('✅ Showing idle warning modal');

    clearTimers();
    countdownRef.current = MODAL_TIMEOUT_SECONDS;

    isModalActiveRef.current = true;
    setIsModalVisible(true);

    console.log('Starting countdown from:', MODAL_TIMEOUT_SECONDS);

    setTimeout(() => {
      // This timer is required to do smooth handling
      intervalRef.current = setTimeout(
        () => {
          // Set a single timeout to handle the logout
          console.log('Starting countdown completed:');
          setIsModalVisible(false);
          isModalActiveRef.current = false;
          handleLogout('User inactivity timeout');
        },
        MODAL_TIMEOUT_SECONDS * 1000 - 5000
      ); // Convert seconds to milliseconds
    }, 5000);
  }, [handleLogout, clearTimers, isModalVisible]);

  // Reset timer utility
  const onResetTimer = useCallback(() => {
    clearTimers();
  }, [clearTimers]);

  // Initialize idle timer
  // Initialize idle timer
  const { activate, reset } = useIdleTimer({
    timeout: INACTIVITY_TIME_LIMIT,
    onIdle: onInactive,
    onActive: () => {
      console.log('🟢 User became ACTIVE at:', new Date().toISOString());
    },
    onAction: (event) => {
      console.log('👆 User ACTION detected:', {
        type: event?.type || 'unknown',
        timestamp: new Date().toISOString(),
        target: (event?.target as any)?.tagName || 'unknown',
      });
    },
    debounce: 500,
    events: [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      // Exclude 'focus', 'blur', 'visibilitychange'
    ],
  });

  const handleModalClose = useCallback(async () => {
    console.log('User chose to stay - checking if token refresh is needed...');

    // Clear the logout countdown timer first
    setTimeout(() => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 5000);

    // Reset modal state FIRST - this is crucial
    isModalActiveRef.current = false;
    isRefreshingRef.current = false;
    setIsModalVisible(false);
    setModalCountdown(MODAL_TIMEOUT_SECONDS);

    console.log(
      'Modal state reset - isRefreshingRef:',
      isRefreshingRef.current,
      'isAuthenticated:',
      isAuthenticated,
      'accountId:',
      accountId
    );

    // Check if token needs refreshing before restarting idle timer
    try {
      const tokensString = ls.get('tokens');
      if (tokensString) {
        const tokens =
          typeof tokensString === 'string'
            ? JSON.parse(tokensString)
            : tokensString;
        const currentTime = Date.now();
        const expiryTime = parseInt(tokens.expiryTime);
        const timeLeft = expiryTime - currentTime;

        console.log('Token check before restarting idle timer:', {
          currentTime: new Date(currentTime).toISOString(),
          expiryTime: new Date(expiryTime).toISOString(),
          timeLeftMs: timeLeft,
          timeLeftSecond: timeLeft / 1000,
          timeLeftMin: timeLeft / 1000 / 60,
          needsRefresh: timeLeft <= TOKEN_REFRESH_BUFFER,
          TOKEN_REFRESH_BUFFER: TOKEN_REFRESH_BUFFER,
          isRefreshingState: isRefreshingRef.current,
          isAuthenticated: isAuthenticated,
          accountId: accountId,
        });

        console.log('🔍 Checking refresh condition:', {
          timeLeft: timeLeft,
          TOKEN_REFRESH_BUFFER: TOKEN_REFRESH_BUFFER,
          condition1: timeLeft <= TOKEN_REFRESH_BUFFER,
          condition2: timeLeft > 0,
          bothConditions: timeLeft <= TOKEN_REFRESH_BUFFER && timeLeft > 0,
        });

        // If token is within refresh threshold, refresh it first
        if (timeLeft <= TOKEN_REFRESH_BUFFER && timeLeft > 0) {
          console.log(
            '✅ Token needs refresh before restarting idle timer - Calling refreshToken now...'
          );

          console.log('Pre-refresh check:', {
            isRefreshingRef: isRefreshingRef.current,
            isAuthenticated: isAuthenticated,
            accountId: accountId,
            customerId: customerId,
          });

          console.log('🔄 Manually triggering token refresh...');

          if (isAuthenticated && accountId) {
            isRefreshingRef.current = true;
            console.log('📤 Dispatching useraccountDetailsRequest...');
            dispatch(useraccountDetailsRequest({ accountId, customerId }));
            lastRefreshTimeRef.current = Date.now();

            setTimeout(() => {
              if (mounted.current) {
                isRefreshingRef.current = false;
              }
            }, 1000);
          } else {
            console.log(
              '❌ Cannot refresh - missing authentication or accountId'
            );
          }

          // Wait for token refresh to complete before restarting timers
          setTimeout(() => {
            console.log(
              '⏰ Restarting idle timer and session validation after token refresh'
            );

            // Restart session validation interval
            if (sessionValidationIntervalRef.current) {
              clearInterval(sessionValidationIntervalRef.current);
            }
            sessionValidationIntervalRef.current = setInterval(() => {
              if (!isModalActiveRef.current) {
                console.log('🔄 30-second validation check...');
                validateSession();
              }
            }, TOKEN_CHECK_INTERVAL);

            activate();
          }, 3000);
        } else {
          console.log('✅ Token is valid, restarting timers immediately');

          // Restart session validation interval
          if (sessionValidationIntervalRef.current) {
            clearInterval(sessionValidationIntervalRef.current);
          }
          sessionValidationIntervalRef.current = setInterval(() => {
            if (!isModalActiveRef.current) {
              console.log('🔄 30-second validation check...');
              validateSession();
            }
          }, TOKEN_CHECK_INTERVAL);

          activate();
        }
      } else {
        console.log('❌ No tokens found, restarting idle timer');
        activate();
      }
    } catch (error) {
      console.error('❌ Error checking token before restart:', error);
      activate();
    }
  }, [
    isAuthenticated,
    accountId,
    customerId,
    dispatch,
    activate,
    validateSession,
  ]);

  // Handle modal dismiss (user wants to logout)
  const handleModalDismiss = useCallback(() => {
    console.log('User chose to end session');
    handleLogout('User chose to logout');
  }, [handleLogout]);

  // Setup session validation interval and cleanup
  useEffect(() => {
    mounted.current = true;

    // Only set up interval checks if authenticated
    if (isAuthenticated) {
      // Initial session validation
      validateSession();

      // Set up periodic session validation
      sessionValidationIntervalRef.current = setInterval(() => {
        if (!isModalActiveRef.current) {
          validateSession();
        }
      }, TOKEN_CHECK_INTERVAL);
    }

    return () => {
      mounted.current = false;
      clearTimers();
      isModalActiveRef.current = false;
    };
  }, [isAuthenticated, validateSession, clearTimers]);

  // Auto-logout when modal countdown reaches 0
  useEffect(() => {
    if (modalCountdown === 0 && isModalVisible) {
      handleLogout('Modal timeout - no user response');
    }
  }, [modalCountdown, isModalVisible, handleLogout]);

  if (!isAuthenticated || !accountDetails) {
    const locale = localStorage.getItem('language') === 'fr' ? 'fr' : 'en';
    // Skip next= on sign-out to avoid returning to the previous screen
    if (isSessionTimeout) {
      return <Navigate to={`/sign-in?locale=${locale}`} replace />;
    }
    const next = `${location.pathname}${location.search}${location.hash}`;
    const signInUrl = buildSignInRedirectUrl(next);
    return <Navigate to={signInUrl} replace />;
  }

  return (
    <>
      {element}
      {isModalVisible && (
        <FAQsModal
          isFAQsModalOpen={isModalVisible}
          setIsFAQsModalOpen={setIsModalVisible}
          onClose={handleModalClose}
          onLogoutClicked={handleLogout}
        />
      )}
    </>
  );
};

export default ProtectedRoute;
